import { broker } from '#~/agent/broker.js';
import { ConnectionStatus } from '#~/common/connection-status/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import * as Dropdown from '@studio/design-system/dropdown-menu';
import { Icon } from '@studio/design-system/icon';
import type { Agent } from '@studio/models/agent';
import { clsx } from 'cn';
import type { ReactElement } from 'react';

/**
 * Helper function to determine if an agent is online based on its status
 */
const isAgentOnline = (status: Agent['status']): boolean => {
  return status === 'online' || status === 'idle' || status === 'booting';
};

interface BridgeDropdownProps {
  machineName: string;
  isPtzoptics: boolean;
  onSearch?: () => void;
  agents: { id: Agent['id']; name: Agent['name']; status: Agent['status'] }[];
  machineAgentId: Agent['id'] | undefined;
  onlineStatus: boolean;
  showLinkAnotherBridge: boolean;
}

/** `BridgeDropdown` component */
export const BridgeDropdown = ({
  agents,
  machineName,
  isPtzoptics,
  onSearch,
  machineAgentId,
  onlineStatus,
  showLinkAnotherBridge
}: BridgeDropdownProps): ReactElement => {
  const handleBridgeSelect = async (agentId: Agent['id']) => {
    if (!agentId) {
      return;
    }

    // State will be synced through the proxy system
    const proxy = broker.acquire(agentId);
    if (!proxy) return;

    if (agentId === machineAgentId) {
      void proxy.emit('bridge.selected', null);
      return;
    }

    // Emit state change event
    void proxy.emit('bridge.selected', agentId);
  };

  return (
    <>
      <Dropdown.Root>
        <Dropdown.Trigger
          className={clsx('flex h-32 flex-grow items-center justify-between rounded-4 px-8', {
            'data-theme': isPtzoptics ? 'ptz-optics-light' : 'dark',
            'bg-base-50': !isPtzoptics,
            'bg-base-200': isPtzoptics
          })}
        >
          <ConnectionStatus connected={onlineStatus} className='mr-8 text-[8px]' />
          {machineName}
          <Icon
            className='ml-auto transition-transform group-x-open:rotate-180'
            icon='chevron-up'
          />
        </Dropdown.Trigger>
        <Dropdown.Content
          data-theme={isPtzoptics ? 'ptz-optics-light' : 'dark'}
          align='start'
          side='bottom'
          className='z-100 w-[var(--radix-dropdown-menu-trigger-width)] text-left'
        >
          {agents.map((agent) => {
            const agentOnlineStatus = isAgentOnline(agent.status);
            return (
              <Dropdown.Item key={agent.id} onClick={() => handleBridgeSelect(agent.id)}>
                <ConnectionStatus connected={agentOnlineStatus} className='text-[8px]' />
                <div>
                  {agent.name} {agent.id === machineAgentId && '(This Computer)'}
                </div>
              </Dropdown.Item>
            );
          })}
          {isElectronApp() && showLinkAnotherBridge && (
            <Dropdown.Item onClick={onSearch}>
              <div>Link Another Bridge</div>
              <i className='i-ion-plus absolute right-[9px] text-lg text-white' color='white' />
            </Dropdown.Item>
          )}
        </Dropdown.Content>
      </Dropdown.Root>
    </>
  );
};
